import Tracker from './Tracker';
import * as zanalytics from '@zillow/universal-analytics';
import type { initTracker } from './Tracker';
import trackerIds from '../constants/trackerIds';

export interface zgOptionsInterface {
  apiKey?: string;
  apiHost?: string;
  secure?: boolean;
  anonymousId?: string;
  writeKey?: string;
}
export interface optionsInterface {
  [key: string]: zgOptionsInterface;
}

interface initDspTracker extends initTracker {
  anonymousId: string;
  apiKey: string;
}
interface TrackEventInterface {
  action: string;
  category: string;
  label?: string;
  value?: number;
  nonInteraction?: boolean;
  transport?: string;
  clickstreamEvent?: Record<string, unknown> & { envelope?: { event_type_id?: string } };
}

interface TrackPageViewInterface {
  path: string;
  dspPageViewProperties?: PageViewPropertiesInterface;
  dspPageViewOptions?: PageViewOptionsInterface;
  clickstreamEvent?: Record<string, unknown> & { envelope?: { event_type_id?: string } };
}

interface PageViewPropertiesInterface {
  [key: string]: string;
}

interface PageViewOptionsInterface {
  [key: string]: unknown;
  integrations?: Record<string, unknown>;
}

export default class Dsp extends Tracker {
  public options: optionsInterface;

  constructor(args: initDspTracker) {
    super({ ...args, type: 'dsp' });

    const { anonymousId, apiKey } = args;
    const { 1: parsedApiKey } = apiKey.split('-');

    const isProd = this?.id === trackerIds.ids.dsp.prod;
    this.options = {
      zillow: {
        apiKey: parsedApiKey,
        apiHost: isProd ? 'e.zg-api.com' : 'analytics-zg-api.test.zillow.net',
        secure: true,
        anonymousId,
        writeKey: isProd ? 'zrm_prod_web' : 'zrm_dev_web',
      },
    };

    if (typeof window === 'undefined') {
      return;
    }

    zanalytics.initZanalytics(window, document, this.options, 'zanalytics');
  }

  setCustomDimensions = (dimensionsObject: Record<string, unknown>): void => {
    if (typeof window === 'undefined') {
      return;
    }

    if (!this.drymode) {
      zanalytics.setdim(dimensionsObject);
    }
  };

  trackEvent = ({ clickstreamEvent: newLaneEvent, ...remainingFieldObject }: TrackEventInterface): void => {
    if (typeof window === 'undefined') {
      return;
    }

    if (!this.drymode) {
      /**
       * zanalytics.track(properties, options?)
       *    properties - An object containing some required and optional parameters
       *
       * @see https://gitlab.zgtools.net/zillow/npm/universal-analytics/-/tree/master/#ua.track
       */
      if (remainingFieldObject.label) {
        remainingFieldObject.label = String(remainingFieldObject.label);
      }

      if (typeof remainingFieldObject.value !== 'number') {
        this.logger && this.logger.warn('Ua.trackEvent Expected `args.value` arg to be a Number.');

        const numCastedGAValue = Math.round(Number(remainingFieldObject.value));

        remainingFieldObject.value = isNaN(numCastedGAValue) ? undefined : numCastedGAValue;
      }

      if (typeof remainingFieldObject.nonInteraction !== 'undefined') {
        if (typeof remainingFieldObject.nonInteraction !== 'boolean') {
          this.logger && this.logger.warn('Ua.trackEvent `args.nonInteraction` must be a boolean.');

          remainingFieldObject.nonInteraction = undefined;
        }
      }

      if (typeof remainingFieldObject.transport !== 'undefined') {
        if (typeof remainingFieldObject.transport !== 'string') {
          this.logger && this.logger.warn('Ua.trackEvent `args.transport` must be a string.');

          remainingFieldObject.transport = undefined;
        } else {
          if (['beacon', 'xhr', 'image'].indexOf(remainingFieldObject.transport) === -1) {
            this.logger &&
              this.logger.warn(
                'Ua.trackEvent `args.transport` must be either one of these values: `beacon`, `xhr` or `image`',
              );
          }
        }
      }

      // disable type checking since @zillow-types/universal-analytics doesn't support clickstream newlane properties yet
      zanalytics.track(remainingFieldObject, {
        // @ts-ignore
        newLaneEvent,
        integrations: {
          GaPlugin: Boolean(newLaneEvent?.envelope?.event_type_id),
          // @ts-ignore
          // NOTE: safe to pass 'uaZrm' only since other 'ua's are not used (check inside 'MultiAnalyticsModule' for more context)
          GaTrackerId: newLaneEvent?.envelope?.event_type_id ? 'uaZrm' : '',
        },
      });
    }
  };

  trackPageView = ({ path = '', clickstreamEvent: newLaneEvent, ...data }: TrackPageViewInterface): void => {
    if (typeof window === 'undefined') {
      return;
    }

    const properties = data.dspPageViewProperties;
    const options = newLaneEvent
      ? {
          newLaneEvent,
          integrations: {
            ...(data?.dspPageViewOptions?.integrations || {}),
            GaPlugin: Boolean(newLaneEvent?.envelope?.event_type_id),
            GaTrackerId: newLaneEvent?.envelope?.event_type_id ? 'uaZrm' : '',
          },
        }
      : data.dspPageViewOptions;

    if (!this.drymode) {
      const trimmedPath = path.trim();

      /**
       * zanalytics.page(path, properties?, options?)
       *    path - The path portion of a URL. This value should start with a slash (/) character.
       *    properties? - An object which can contain optional dimensions and metrics.
       *    options? - An optional parameter used to pass additional data to zanalytics. See the optional options? parameter section for more details.
       *
       * @see https://gitlab.zgtools.net/zillow/npm/universal-analytics/-/tree/master#ua.page
       */
      // @ts-ignore
      zanalytics.page(trimmedPath as string, properties, options);
    }
  };
}
